import { useAppDispatch, useAppSelector } from 'app/hooks';
import FormInput from 'components/common/form-input';
import Button from 'components/inputs/button';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import { ConfidenceScoreConfigType } from 'utils/confidence-score';
import {
  ConfidenceScoreSettingsDraft,
  validateConfidenceScoreSettings
} from 'utils/confidence-score-validation';
import { errorModal, successModal } from 'utils/ui';

// Self-contained 'Confidence Score' settings block (AHA-6796 memo §10):
// weights, per-status points, the hard-gate DCS cap, band thresholds, and
// the hardGate mode/threshold (notify shows a Choose Systems reminder;
// quote sending is not gated by score). The have/want field catalogs are
// rendered read-only v1 (editable lists are follow-up fodder). Save
// merges the scalar edits over the stored config so the catalogs are
// preserved verbatim; without a stored row Save stays disabled — the
// settings PATCH endpoint is UPDATE-only, so the seed migration must run
// first.

const draftFromConfig = (
  config: ConfidenceScoreConfigType
): ConfidenceScoreSettingsDraft => ({
  haveWeight: config.haveWeight,
  wantWeight: config.wantWeight,
  requiredPoints: config.points.required,
  conditionalPoints: config.points.conditional,
  recommendedPoints: config.points.recommended,
  optionalPoints: config.points.optional,
  capValue: config.capValue,
  hardGateMode: config.hardGate.mode,
  hardGateThreshold: config.hardGate.threshold,
  bands: [...config.bands]
    .sort((a, b) => b.min - a.min)
    .map((band) => ({ key: band.key, label: band.label, min: band.min }))
});

export default function ConfidenceScoreSettings() {
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.admin.settings);
  const config = settings?.confidenceScore ?? null;
  const [draft, setDraft] = useState<ConfidenceScoreSettingsDraft | null>(null);

  useEffect(() => {
    if (config) {
      setDraft(draftFromConfig(config));
    }
  }, [config]);

  if (!config || !draft) {
    return (
      <div className='flex flex-col gap-2'>
        <div className='text-xl font-bold text-gray-900'>Confidence Score</div>
        <div className='text-gray-500'>
          The confidenceScore setting row was not found — run the seed migration
          first, then reload this page.
        </div>
      </div>
    );
  }

  const errors = validateConfidenceScoreSettings(draft);
  const hasErrors = Boolean(errors.fields || errors.bands);

  const setField = <K extends keyof ConfidenceScoreSettingsDraft>(
    key: K,
    value: ConfidenceScoreSettingsDraft[K]
  ) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const setBandMin = (index: number, value: number | undefined) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const bands = [...prev.bands];
      bands[index] = { ...bands[index], min: value };
      return { ...prev, bands };
    });
  };

  const handleSave = async () => {
    if (hasErrors) {
      return;
    }
    // Scalar edits merged over the stored config — the field catalogs ride
    // through verbatim.
    const next: ConfidenceScoreConfigType = {
      ...config,
      haveWeight: Number(draft.haveWeight),
      wantWeight: Number(draft.wantWeight),
      points: {
        required: Number(draft.requiredPoints),
        conditional: Number(draft.conditionalPoints),
        recommended: Number(draft.recommendedPoints),
        optional: Number(draft.optionalPoints)
      },
      capValue: Number(draft.capValue),
      hardGate: {
        mode: draft.hardGateMode,
        threshold: Number(draft.hardGateThreshold)
      },
      bands: draft.bands.map((band) => ({
        min: Number(band.min),
        key: band.key,
        label: band.label
      }))
    };
    try {
      await axios.patch('settings', [
        { key: 'confidenceScore', value: JSON.stringify(next) }
      ]);
      dispatch(fetchDefaultAdminData());
      successModal('Settings updated.');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const numberField = (
    key: keyof ConfidenceScoreSettingsDraft,
    label: string,
    step = 1
  ) => (
    <div className='flex flex-col gap-1'>
      <FormInput
        type='number'
        label={label}
        value={draft[key]}
        onChange={(value) => setField(key, value)}
        step={step}
        min={0}
        required
      />
      {errors.fields?.[key] && (
        <div className='text-sm text-red-500'>{errors.fields[key]}</div>
      )}
    </div>
  );

  return (
    <div className='flex flex-col gap-5'>
      <div className='text-xl font-bold text-gray-900'>Confidence Score</div>
      <div className='text-gray-500'>
        How the recommendation confidence score is computed: side weights,
        points per field tier, the hard-gate cap on data completeness, and the
        display bands. Quotes can be sent regardless of score. Notify mode shows
        a reminder on Choose Systems when the score is below the threshold.
      </div>
      <div className='flex max-w-3xl flex-col gap-4'>
        <div className='flex flex-wrap items-start gap-3 rounded-lg bg-white p-3'>
          <div className='w-28 pt-9 font-semibold text-gray-900'>Weights</div>
          {numberField('haveWeight', 'Have (data)', 0.05)}
          {numberField('wantWeight', 'Want (preferences)', 0.05)}
        </div>
        <div className='flex flex-wrap items-start gap-3 rounded-lg bg-white p-3'>
          <div className='w-28 pt-9 font-semibold text-gray-900'>Points</div>
          {numberField('requiredPoints', 'Required')}
          {numberField('conditionalPoints', 'Conditional')}
          {numberField('recommendedPoints', 'Recommended')}
          {numberField('optionalPoints', 'Optional')}
        </div>
        <div className='flex flex-wrap items-start gap-3 rounded-lg bg-white p-3'>
          <div className='w-28 pt-9 font-semibold text-gray-900'>Hard Gate</div>
          {numberField('capValue', 'DCS cap')}
          <div className='flex flex-col gap-1'>
            <FormInput
              type='select'
              label='Mode'
              value={draft.hardGateMode}
              onChange={(value) =>
                setField(
                  'hardGateMode',
                  value === 'notify' ? 'notify' : 'block'
                )
              }
              options={[
                { label: 'No reminder', value: 'block' },
                { label: 'Notify on Choose Systems', value: 'notify' }
              ]}
              preventDeselection
              required
            />
          </div>
          {numberField('hardGateThreshold', 'Threshold')}
        </div>
        <div className='flex flex-wrap items-start gap-3 rounded-lg bg-white p-3'>
          <div className='w-28 pt-9 font-semibold text-gray-900'>Bands</div>
          {draft.bands.map((band, index) => (
            <div key={band.key} className='flex flex-col gap-1'>
              <FormInput
                type='number'
                label={`${band.label} from`}
                value={band.min}
                onChange={(value) => setBandMin(index, value)}
                step={1}
                min={0}
                max={100}
                required
              />
              {errors.bands?.[index] && (
                <div className='text-sm text-red-500'>
                  {errors.bands[index]}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className='flex flex-col gap-3 rounded-lg bg-white p-3'>
          <div className='font-semibold text-gray-900'>
            Scored fields (read-only)
          </div>
          <div className='text-sm text-gray-500'>
            The equipment and preference fields the score walks. Editing these
            lists is planned as a follow-up.
          </div>
          <div className='flex flex-col gap-2'>
            {Object.entries(config.haveFields).map(
              ([componentType, fields]) => (
                <div key={componentType} className='text-sm'>
                  <span className='font-medium text-gray-700'>
                    {componentType}:
                  </span>{' '}
                  <span className='text-gray-500'>
                    {fields.length === 0
                      ? 'no scored fields'
                      : fields
                          .map(
                            (field) =>
                              `${field.label} (${field.status}` +
                              `${field.defaultSafe ? ', default-safe' : ''}` +
                              `${
                                (
                                  config.hardGateFields[componentType] ?? []
                                ).includes(field.key)
                                  ? ', hard gate'
                                  : ''
                              })`
                          )
                          .join(', ')}
                  </span>
                </div>
              )
            )}
            <div className='text-sm'>
              <span className='font-medium text-gray-700'>
                Customer preferences:
              </span>{' '}
              <span className='text-gray-500'>
                {config.wantFields
                  .map((field) => `${field.label} (${field.status})`)
                  .join(', ')}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={hasErrors}
        />
      </div>
    </div>
  );
}
